import {
    drillThruAllowed,
    findDrillThruDataPointForChart,
    getAgeDisplayfromDays,
    getChartDisplayTypeForViewerType,
    getChartTitle,
    getCustomColorSeries,
    getCustomColorSeriesForGroupChart,
    getDurationDisplayFromHours,
    getMultipleMeasureTooltip,
    getPieLegendTitle,
    percentageChangePerBar,
    percentageTrend,
    valueFormatter,
} from '../Charts/chartHelperFunctions'
import { FoundationsEnums } from '../../enum'
import { getPieOptions } from './pieChartOptions'
import { scatterPlotOptions } from './scatterPlotOptions'
import { RunVisualUnitWithId_VisualUnit } from '../../Interfaces/APIContracts/RunVisualUnitWithId_VisualUnit'

const X_AXIS_VALUE = -250
const Y_AXIS_VALUE = 100
const Y_AXIS_VALUE_BAR = 100
const MARGIN_3D_RIGHT = -40
const MARGIN_3D_LEFT = -80
export const SPACING_TOP = 50

export const getChartOptions = (
    vuId: number,
    chartDisplayType: number,
    ExecuteResponse: RunVisualUnitWithId_VisualUnit,
    enable3dview: boolean,
    drawing: boolean,
    showLegend: boolean,
    vuChartWidth: number,
    openDrillThru: (cellInfo: any, cell: any, row: any, rowIndex: any) => void,
    drillThruDPList: { [s: string]: any }[],
    userTypeId:string,
    alphaCurrent?: number,
    betaCurrent?: number,
) => {
    const arrMultipleAxisColor = ['#4572A7', '#89A54E', '#AA4643', '#870FA5', '#C7891F']
    const chartDisplayName = getChartDisplayTypeForViewerType(chartDisplayType)

    let numBarsInXAxis = 1
    const vuChartVariationType = ExecuteResponse.chartVariationType
    const vuChartData = ExecuteResponse.chartData
    const vuChartDetails = ExecuteResponse.vuDetails
    const vuChartSettings = ExecuteResponse.chartSettings
    const scaleIdToSeriesIndexMap: number[] = []
    const scaleIdToSeriesIndexMap_Yaxis_opp: boolean[] = []
    const num_series = Object.keys(vuChartData.dataSeries).length
    const drillThruChartPoint = (seriesName: string, categoryName: string, pointValue: any) => {
        const drillThruDP = findDrillThruDataPointForChart(
            vuId,
            seriesName,
            categoryName,
            drillThruDPList,
            vuChartDetails.MeasureList
        )
        const cellValue = String(pointValue)
        openDrillThru(drillThruDP, cellValue, -1, -1)
    }
    if (chartDisplayName == 'pie') {
        return getPieOptions(
            vuId,
            vuChartDetails,
            vuChartVariationType,
            vuChartSettings,
            vuChartData,
            enable3dview,
            showLegend,
            vuChartWidth,
            userTypeId,
            drillThruChartPoint
        )
    }
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Awesome trick to get the formatter dynamically after the chart is filled with series data.
    // For more information refer to: http://jsfiddle.net/tUV98/1/

    // TODO!! tooltip dynamic formatter
    const fluidTooltipFormatter = function (formatter: {
        x: number
        y: number
        formatter?: Function
        percentage?: number | undefined
        series?: any
    }) {
        let labelVal = ''
        if (vuChartDetails.MeasureList.length == 1 || vuChartData.dataSeries[0].Scatterdata[0]) {
            if (vuChartData.dataSeries[0].Scatterdata[0]) {
                let dimensionData = {}
                let dimensionName = ''
                vuChartData.dataSeries.forEach(function (dataSeries: { Scatterdata: any }) {
                    dataSeries.Scatterdata.forEach(function (dataPoint: {
                        index: any
                        value: any
                        dimension: any
                        dimensionName: any
                    }) {
                        if (formatter.x == dataPoint.index && formatter.y == dataPoint.value) {
                            dimensionData = dataPoint.dimension
                            dimensionName = dataPoint.dimensionName
                        }
                    })
                })

                labelVal =
                    dimensionName.toString() +
                    ': <strong>' +
                    dimensionData.toString() +
                    '</strong><br>'
                let dataTypeStr = vuChartDetails.MeasureList[0].MetaData.DataType.toString()

                let percStr = ''
                if (formatter.percentage != undefined) {
                    percStr = ' (' + Math.round(formatter.percentage) + '%)'
                }

                labelVal +=
                    vuChartData.xAxis.title +
                    ': <strong>' +
                    valueFormatter(formatter.x, dataTypeStr) +
                    percStr +
                    '</strong>'
                labelVal += '<br>'

                dataTypeStr = vuChartDetails.MeasureList[1].MetaData.DataType.toString()

                percStr = ''
                if (formatter.percentage != undefined) {
                    percStr = ' (' + Math.round(formatter.percentage) + '%)'
                }

                labelVal +=
                    vuChartData.yAxis.title +
                    ': <strong>' +
                    valueFormatter(formatter.y, dataTypeStr) +
                    percStr +
                    '</strong>'
                labelVal += '<br>'
            } else {
                let dataTypeStr = vuChartDetails.MeasureList[0].MetaData.DataType.toString()

                let percStr = ''
                if (formatter.percentage != undefined) {
                    percStr = ' (' + Math.round(formatter.percentage) + '%)'
                }

                labelVal = getChartTitle(formatter, vuChartData, vuChartVariationType)
                labelVal += vuChartData.xAxis.title + ': <strong>' + formatter.x + '</strong><br/>'

                labelVal +=
                    vuChartData.yAxis.title +
                    ': <strong>' +
                    valueFormatter(formatter.y, dataTypeStr) +
                    percStr +
                    '</strong>'
            }
        } else if (vuChartDetails.MeasureList.length >= 2) {
            let dataTypeStrArray = []
            for (let iM = 0; iM < vuChartDetails.MeasureList.length; iM++) {
                let dataTypeStr = vuChartDetails.MeasureList[iM].MetaData.DataType.toString()

                if (vuChartDetails.MeasureList[iM].BusinessParameterId > 0) {
                    if (dataTypeStr == FoundationsEnums.HLDataType.HLBusinessParameter) {
                        dataTypeStr = vuChartDetails.MeasureList[iM].BusinessParameter.DataType
                    }
                }

                dataTypeStrArray.push(dataTypeStr)
            }
            labelVal = getChartTitle(formatter, vuChartData, vuChartVariationType)
            labelVal += getMultipleMeasureTooltip(formatter, vuChartData, dataTypeStrArray)
        }
        return labelVal
    }

    // Create chart type specific options
    // TODO special case for scatter plot options
    const getChartSpecificOpts = (
        chartType: string,
        secondaryOrganizer: boolean,
        grouped?: boolean
    ) => {
        switch (chartType) {
            case 'area':
                return {
                    dataLabels: {
                        enabled: vuChartSettings.ShowChartDataLabel,
                    },
                    depth: 200 / num_series,
                }
            case 'spline':
                return {
                    dataLabels: {
                        enabled: secondaryOrganizer ? undefined : false,
                    },
                }
            case 'bar':
                return {
                    stacking: grouped ? undefined : 'normal',
                    borderWidth: 0,
                }
            case 'column':
                return {
                    stacking: grouped ? undefined : 'normal',
                    borderWidth: 0,
                }
            default:
                return {}
        }
    }

    const isMultiMeasureChart = (vuChartVariationType: string) => {
        switch (vuChartVariationType) {
            case 'C_2M_1X_or_1Y':
            case 'C_3M_1X_or_1Y':
            case 'C_4M_1X_or_1Y':
            case 'C_5M_1X_or_1Y':
            case 'C_2M_1X_and_1Y':
            case 'C_3M_1X_and_1Y':
            case 'C_4M_1X_and_1Y':
            case 'C_5M_1X_and_1Y':
            case 'C_NM_2D':
            case 'C_NM_1D':
                return true
            default:
                return false
        }
    }

    // For YAxis Label Formatter (for single measure)
    const arrayYAxisLabelFormatter: ((formatter: { value?: any }) => any)[] = []
    const fluidYAxisLabelFormatter = function (formatter: { value?: any }) {
        let labelVal = formatter.value
        if (vuChartDetails.MeasureList.length == 1) {
            let dataTypeStr = vuChartDetails.MeasureList[0].MetaData.DataType.toString()
            labelVal = valueFormatter(formatter.value, dataTypeStr)
        }
        return labelVal
    }
    arrayYAxisLabelFormatter.push(fluidYAxisLabelFormatter)

    const chartName =
        enable3dview && chartDisplayName == 'line'
            ? 'area'
            : chartDisplayName == 'line'
              ? 'spline'
              : chartDisplayName == 'stackedBar'
                ? 'bar'
                : chartDisplayName == 'stackedColumn'
                  ? 'column'
                  : chartDisplayName == 'columnGroup'
                    ? 'column'
                    : chartDisplayName

    // TODO handle pie chart special cases
    let chartOptions: { [s: string]: any } = {
        credits: {
            enabled: false,
        },
        title: {
            text: '',
            style: {
                fontFamily: 'Helvetica',
                fontSize: '14px',
            },
        },
        exporting: {
            enabled: true,
            type: 'image/png',
            filename: 'healthlevel_chart',
            buttons: {
                printButton: {
                    enabled: false,
                },
                exportButton: {
                    enabled: false,
                },
            },
        },
        navigation: {
            buttonOptions: {
                enabled: false,
            },
        },
        chart: {
            animation: vuChartSettings.ShowChartAnimation,
            type: chartName,
            spacingTop: SPACING_TOP,
            marginLeft: enable3dview ? MARGIN_3D_LEFT : undefined,
            marginRight: enable3dview ? MARGIN_3D_RIGHT : undefined,
            //type: (chartDisplayType == "Line" ? "spline" : "bar"),
            zoomType: enable3dview || drawing ? undefined : 'xy',
            resetZoomButton: {
                position: {
                    verticalAlign: 'bottom',
                    x: -20,
                    y: -50,
                },
            },
            alignTicks: false,
            options3d: {
                enabled: enable3dview,
                alpha: alphaCurrent !== undefined ? alphaCurrent : 15,
                beta: betaCurrent !== undefined ? betaCurrent : 15,
                depth: 50,
                viewDistance: 25,
            },
        },
        plotOptions: {
            series: {
                animation: vuChartSettings.ShowChartAnimation,
                stacking:
                    chartDisplayName != 'stackedBar' && chartDisplayName != 'stackedColumn'
                        ? undefined
                        : 'normal',
                dataLabels: {
                    enabled: vuChartSettings.ShowChartDataLabel,
                    style: {
                        // color: 'black',
                        textOutline: '0px contrast',
                        'stroke-width': 0,
                    },
                },
                cursor: drillThruAllowed(
                    0,
                    vuChartDetails.AllowDrillThru,
                    vuChartDetails.MeasureList,
                    userTypeId
                )
                    ? 'pointer'
                    : 'default',
                point: {
                    events: {
                        click: function (event: { point: { [s: string]: any } }) {
                            if (
                                !drillThruAllowed(
                                    event.point.series.index,
                                    vuChartDetails.AllowDrillThru,
                                    vuChartDetails.MeasureList,
                                    userTypeId
                                )
                            )
                                return

                            drillThruChartPoint(
                                event.point.series.name,
                                event.point.category,
                                event.point.y
                            )
                        },
                    },
                },
            },
        },
        xAxis: {
            categories: vuChartData.xAxis.category,
            title: {
                text: vuChartData.xAxis.title,
                y: enable3dview ? Y_AXIS_VALUE : 0,
            },
            labels: {
                enabled: true,
                y: 3,
                zIndex: 6,
                rotation: 0,
                align: '',
            },
            zoomEnabled: !drawing,
        },
        yAxis:
            !isMultiMeasureChart(vuChartVariationType) || chartDisplayName === 'scatter'
                ? {
                      title: {
                          text: vuChartData.yAxis.title,
                          //   x: enable3dview ? MARGIN_3D_LEFT : 0,
                          //   margin: enable3dview ? 60 : 40,
                      },
                      labels: {
                          formatter: function () {
                              return arrayYAxisLabelFormatter[0](this)
                          },
                          zIndex: 6,
                      } as { formatter: Function; zIndex: number; value?: any },
                      zoomEnabled: !drawing,
                  }
                : [],
        tooltip: {
            x: 0,
            y: 0,
            formatter: function () {
                return fluidTooltipFormatter(this)
            },
        },
        legend: {
            floating: false,
            draggable: false,
            enabled: showLegend,
            borderWidth: 0,
            borderRadius: 2,
            title: {
                text: '',
            },

            layout: 'vertical',
            align: 'right',
            y: -18,
            itemWidth: 125,
            verticalAlign: 'top',
            backgroundColor: '#FFFFFF',
        },
        series: [{ data: [] }] as { [s: string]: any }[],
    }

    // TODO handle primary vs seondary measure case
    chartOptions.plotOptions[chartName] = getChartSpecificOpts(
        chartName,
        false,
        chartDisplayName != 'stackedBar' && chartDisplayName != 'stackedColumn'
    )

    if (isMultiMeasureChart(vuChartVariationType) && chartDisplayName !== 'scatter') {
        chartOptions.plotOptions.spline = {
            animation: vuChartSettings.ShowChartAnimation,
        }

        let oppaxis = false
        vuChartData.dataSeries.forEach(function (
            dataSeries: { data: any; name: string; measuretype: any },
            index_i: number
        ) {
            const dataPoints: number[] = []

            let ymin = Number.MAX_VALUE
            let ymax = Number.MIN_VALUE
            let datapointt = 0.0
            dataSeries.data.forEach(function (dataPoint: string) {
                datapointt = parseFloat(dataPoint)
                dataPoints.push(datapointt)

                if (ymin > datapointt) ymin = datapointt
                if (ymax < datapointt) ymax = datapointt
            })

            // Add series options
            chartOptions.series[index_i] = {}
            chartOptions.series[index_i].name = dataSeries.name
            chartOptions.series[index_i].data = dataPoints

            // TODO !! handle drillthru allowed
            chartOptions.series[index_i].cursor = drillThruAllowed(
                index_i,
                vuChartDetails.AllowDrillThru,
                vuChartDetails.MeasureList,
                userTypeId
            )
                ? 'pointer'
                : 'default'
            chartOptions.series[index_i].dataLabels = {}
            chartOptions.series[index_i].dataLabels.enabled = vuChartSettings.ShowChartDataLabel
            chartOptions.series[index_i].dataLabels.inside = false

            chartOptions.series[index_i].dataLabels.formatter = function () {
                return valueFormatter(this.y, dataSeries.measuretype)
            }
            if (
                scaleIdToSeriesIndexMap[vuChartDetails.MeasureList[index_i].MetaData.ScaleId] ==
                null
            ) {
                scaleIdToSeriesIndexMap[vuChartDetails.MeasureList[index_i].MetaData.ScaleId] =
                    index_i
                scaleIdToSeriesIndexMap_Yaxis_opp[
                    vuChartDetails.MeasureList[index_i].MetaData.ScaleId
                ] = oppaxis
                oppaxis = true
            }
            // Set the correct scale id / series id
            if (vuChartDetails.MeasureList[index_i].MetaData.ScaleId != 0) {
                chartOptions.series[index_i].yAxis =
                    scaleIdToSeriesIndexMap[vuChartDetails.MeasureList[index_i].MetaData.ScaleId]
            } else {
                chartOptions.series[index_i].yAxis = index_i
            }

            if (chartDisplayName == 'Line') {
                if (index_i == 0) {
                    chartOptions.series[index_i].type = 'spline'
                    chartOptions.series[index_i].dashStyle = 'ShortDashDot'
                }
            }

            // This is setting the +1 series to line/spline, the first series is a column
            if (index_i >= 1) {
                chartOptions.series[index_i].type = 'spline'
            }

            chartOptions.series[index_i].color = arrMultipleAxisColor[index_i]
            if (index_i == 2) {
                chartOptions.series[index_i].dashStyle = 'ShortDot'
            }

            if (vuChartSettings.showPercentageTrend) {
                percentageChangePerBar(dataSeries, chartOptions, vuChartSettings, index_i)
            }
            if (dataSeries.name == 'PERCENTAGE') {
                percentageTrend(dataSeries, chartOptions, vuChartSettings, index_i)
            }

            // Set yAxis options
            const styleObj = { color: arrMultipleAxisColor[index_i] }

            const titleObj = {
                text: dataSeries.name,
                margin: 50,
            } as { text: string; margin: number; style: object }

            const labelsObj = {
                margin: 0,
                zIndex: 6,
                formatter: (_: any) => {},
            } as any // 'any' is needed here because this is interfacing with highcharts types

            let dataTypeStr = vuChartDetails.MeasureList[index_i].MetaData.DataType.toString()
            switch (dataTypeStr) {
                case FoundationsEnums.HLDataType.HLDuration:
                    labelsObj.formatter = function () {
                        return getDurationDisplayFromHours(this.value)
                    }
                    break
                case FoundationsEnums.HLDataType.HLAgeDays:
                    labelsObj.formatter = function () {
                        return getAgeDisplayfromDays(this.value)
                    }
                    break
                default:
                    break
            }

            chartOptions.yAxis[index_i] = {}
            if (index_i > 0) {
                chartOptions.yAxis[index_i].opposite =
                    scaleIdToSeriesIndexMap_Yaxis_opp[
                        vuChartDetails.MeasureList[index_i].MetaData.ScaleId
                    ] //true;
                titleObj.style = styleObj
                labelsObj.style = styleObj
                chartOptions.yAxis[index_i].gridLineWidth = 0
                //if (index_i > 0)
            }
            //else {
            //    titleObj.text = vuChartData.yAxis.title;
            //    chartOptions.yAxis.title = titleObj;
            //}

            chartOptions.yAxis[index_i].title = titleObj
            chartOptions.yAxis[index_i].labels = labelsObj
            chartOptions.yAxis[index_i].alignTicks = false
        }) // of $.each
    } else if (chartDisplayName === 'scatter') {
        chartOptions = scatterPlotOptions(
            vuChartVariationType,
            vuChartData,
            vuChartSettings,
            vuChartDetails,
            chartOptions,
            userTypeId
        )
    } else {
        vuChartData.dataSeries.forEach(function (
            dataSeries: { data: any; measuretype: any; name: string },
            index_i: number
        ) {
            const dataPoints: Object[] = []
            dataSeries.data.forEach(function (dataPoint: string, index_j: string | number) {
                const categoryName = vuChartData.xAxis.category[index_j]
                const columnSeries: { [s: string]: any } = {
                    name: categoryName,
                    y: parseFloat(dataPoint),
                }
                const seriesLabel =
                    vuChartData.dataSeries && vuChartData.dataSeries[index_i].name
                        ? vuChartData.dataSeries[index_i].name
                        : ''
                const sliceColor =
                    categoryName !== undefined
                        ? getCustomColorSeriesForGroupChart(
                              getPieLegendTitle(vuChartData),
                              seriesLabel
                          )
                        : getCustomColorSeries(getPieLegendTitle(vuChartData))
                if (sliceColor != '') {
                    columnSeries.color = sliceColor
                }

                dataPoints.push(columnSeries)
            })

            chartOptions.series[index_i] = {}

            if (vuChartSettings.ShowChartDataLabel) {
                chartOptions.series[index_i].dataLabels = {}
                chartOptions.series[index_i].dataLabels.enabled = vuChartSettings.ShowChartDataLabel
                chartOptions.series[index_i].dataLabels.inside =
                    !isMultiMeasureChart(vuChartVariationType)
                // chartOptions.series[index_i].dataLabels.color = 'black'
                chartOptions.series[index_i].dataLabels.formatter = function () {
                    if (vuChartSettings.ShowChartZeroValue == false) {
                        if (this.y > 0) {
                            return valueFormatter(this.y, dataSeries.measuretype)
                        }
                    } else {
                        return valueFormatter(this.y, dataSeries.measuretype)
                    }
                }
            }

            if (vuChartSettings.showPercentageTrend) {
                percentageChangePerBar(dataSeries, chartOptions, vuChartSettings, index_i)
            }

            const titleObj = {
                margin: 5,
                text: '',
                x: 0,
            }
            if (index_i == 0) {
                titleObj.text = vuChartData.yAxis.title
                chartOptions.yAxis.title = titleObj
                chartOptions.yAxis.title.x = enable3dview ? X_AXIS_VALUE : 0
            } else {
                // next y axis .. so put on opposite etc..
            }

            chartOptions.series[index_i].name = dataSeries.name
            chartOptions.series[index_i].measuretype = dataSeries.measuretype
            if (dataSeries.name == 'SUMMATION') {
                chartOptions.series[index_i].type = 'spline'
                if (vuChartSettings.ShowChartDataLabel) {
                    chartOptions.series[index_i].dataLabels = {}
                    chartOptions.series[index_i].dataLabels.enabled = true
                    chartOptions.series[index_i].dataLabels.inside = true
                    chartOptions.series[index_i].dataLabels.color = 'black'
                    chartOptions.series[index_i].dataLabels.formatter = function () {
                        this.measureDataType = dataSeries.measuretype
                        if (vuChartSettings.ShowChartZeroValue == false) {
                            if (this.y > 0) {
                                return valueFormatter(this.y, dataSeries.measuretype)
                            }
                        } else {
                            return valueFormatter(this.y, dataSeries.measuretype)
                        }
                        //return valueFormatter(this.y, dataSeries.measuretype)
                    }
                }
                //chartOptions.series[index_i].type = "spline";
            }

            chartOptions.series[index_i].data = dataPoints
            let title =
                vuChartData.legendTitle != '' ? vuChartData.legendTitle : vuChartData.xAxis.title
            let barColor = getCustomColorSeriesForGroupChart(title, dataSeries.name)
            if (barColor != '') {
                chartOptions.series[index_i].color = barColor
            }

            if (dataSeries.name == 'PERCENTAGE') {
                percentageTrend(
                    dataSeries,
                    chartOptions,
                    vuChartSettings.ShowChartZeroValue,
                    index_i
                )
            }
        })
        if (vuChartData.dataSeries.length == 1 && vuChartSettings.ShowChartSummation == true) {
            chartOptions.series.unshift({
                dataLabels: {
                    enabled: vuChartSettings.ShowChartSummation,
                    inside: false,
                },
            })
        }

        if (
            (vuChartDetails.XAxisList.length == 0 && vuChartDetails.YAxisList.length == 1) ||
            (vuChartDetails.XAxisList.length == 1 && vuChartDetails.YAxisList.length == 0)
        ) {
            chartOptions.legend.enabled = false
        }
    }

    numBarsInXAxis = vuChartData.xAxis.category.length
    if (chartOptions.chart.type != 'bar') {
        chartOptions.xAxis.labels.y = isMultiMeasureChart(vuChartVariationType) ? 30 : 20
    }

    if (numBarsInXAxis > 10) {
        chartOptions.xAxis.labels.rotation = -45
        chartOptions.xAxis.labels.align = 'right'
    }
    if (numBarsInXAxis > 30) {
        // TODO
        if (numBarsInXAxis * 15 > vuChartWidth) {
            if (!isMultiMeasureChart(vuChartVariationType)) {
                chartOptions.xAxis.labels.enabled = false
            } else {
                chartOptions.width = numBarsInXAxis * 25
            }
        }
    }

    return chartOptions
}
